import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Popconfirm, Spin } from 'antd';
import axiosInstance from '../../../axiosInstance';
import * as XLSX from 'xlsx';
import exportToPDF from './reports/product_lists_pdf';
import { BACKENDURL, BACKENDURLFiles } from '../../constants';
import { DownloadOutlined } from '@ant-design/icons';


const { Option } = Select;

const Products = () => {
    const [products, setProducts] = useState([]);
    const [shops, setShops] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [selectedProductDetails, setSelectedProductDetails] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [currentProductIndex, setCurrentProductIndex] = useState(null);
    const [searchParams, setSearchParams] = useState({ name: '', category_id: '', sub_category_id: '' });

    useEffect(() => {
        fetchProducts();
        fetchShops();
        fetchCategories();
    }, [searchParams]);


    const handleImport = async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        setLoading(true); // Show loading spinner

        try {
            const response = await axiosInstance.post('/import-products', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            message.success('Products imported successfully');
            fetchProducts(); // Refresh the products list
            setImportModalVisible(false); // Close the modal
        } catch (error) {
            if (error.response && error.response.status === 422) {
                const errorDetails = error.response.data.errors;
                for (const field in errorDetails) {
                    message.error(`Error in field "${field}": ${errorDetails[field].join(', ')}`);
                }
            } else {
                message.error('Failed to import products. Please try again.');
            }
            console.error(error);
        } finally {
            setLoading(false); // Hide loading spinner after process is complete
        }
    };


    // / Trigger file input click
    const handleFileClick = () => {
        document.getElementById('import-file-input').click();
    };

    const handleSearch = (e) => {
        const { name, value } = e.target;
        setSearchParams(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleDownloadTemplate = (event) => {
        event.preventDefault();

        // Data with headers and example row
        const data = [
            ['name', 'buying_price', 'selling_price', 'expire_date', 'category_name', 'sub_category_name', 'barcode'], // Header row
            // Add more rows as needed
        ];

        // Create a new worksheet
        const ws = XLSX.utils.aoa_to_sheet(data);

        // Apply bold style to the header row using 's' property
        const headerStyle = {
            font: { bold: true }, // Bold font style
        };

        // Iterate over the first row (header row) and apply the style
        for (let col = 0; col < data[0].length; col++) {
            const cell = ws[XLSX.utils.encode_cell({ r: 0, c: col })]; // Get cell at the header row
            if (cell) {
                cell.s = headerStyle; // Apply bold style
            }
        }

        // Create a new workbook with the worksheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Template');

        // Generate the Excel file as a Blob with the correct MIME type
        const excelFile = XLSX.write(wb, {
            bookType: 'xlsx',
            type: 'binary' // Use 'binary' format
        });

        // Convert binary string to a Blob
        const blob = new Blob([s2ab(excelFile)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        // Create a link to trigger the download
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'products.xlsx'; // The name of the file being downloaded
        link.click();
    };

    // Helper function to convert string to ArrayBuffer
    const s2ab = (s) => {
        const buf = new ArrayBuffer(s.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i < s.length; i++) {
            view[i] = s.charCodeAt(i) & 0xFF;
        }
        return buf;
    };


    const handleCategoryChange = (value) => {
        setSearchParams(prevState => ({
            ...prevState,
            category_id: value,
        }));
        fetchSubCategories(value);
    };

    const fetchProducts = async () => {
        setLoading(true);
        try {
            let url = '/products';
            const params = [];

            if (searchParams.name) {
                params.push(`name=${searchParams.name}`);
            }
            if (searchParams.category_id) {
                params.push(`category_id=${searchParams.category_id}`);
            }
            if (searchParams.sub_category_id) {
                params.push(`sub_category_id=${searchParams.sub_category_id}`);
            }

            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await axiosInstance.get(url);
            if (response.data.products) {
                setProducts(response.data.products);
            } else {
                message.error('Failed to fetch products.');
                setProducts([]);
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch products.');
        } finally {
            setLoading(false);
        }
    };

    const fetchShops = async () => {
        try {
            const response = await axiosInstance.get('/shops');
            if (response.data.shops) {
                setShops(response.data.shops);
            } else {
                message.error('Failed to fetch shops.');
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch shops.');
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axiosInstance.get('/categories');
            if (response.data.categories) {
                setCategories(response.data.categories);
            } else {
                message.error('Failed to fetch categories.');
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch categories.');
        }
    };

    const fetchSubCategories = async (categoryId) => {
        setSubCategories([]);
        try {
            const response = await axiosInstance.get(`/categories/${categoryId}/sub-categories`);
            if (response.data.subCategories) {
                setSubCategories(response.data.subCategories);
            } else {
                message.warning('No sub-categories found for this category.');
                setSubCategories([]);
            }
        } catch (error) {
            console.error('Error fetching sub-categories:', error);
            message.error('Failed to fetch sub-categories.');
            setSubCategories([]);
        }
    };

    const showModal = (product = null) => {
        if (product) {
            setEditingProduct(product);
            fetchSubCategories(product.category_id);
            form.setFieldsValue({
                name: product.name,
                buying_price: product.buying_price,
                selling_price: product.selling_price,
                quantity: product.quantity ?? 0,
                expire_date: product.expire_date,
                category_id: product.category.id,
                sub_category_id: product.sub_category?.id,
                barcode: product.barcode,
            });
        } else {
            setEditingProduct(null);
            form.resetFields();
            form.setFieldsValue({ quantity: 0 });
        }
        setIsModalVisible(true);
    };

    const handleOk = async (values) => {
        try {
            const { quantity } = values;
            if (quantity < 0) {
                message.error('Quantity cannot be negative.');
                return;
            }
            let response;
            if (editingProduct) {
                response = await axiosInstance.put(`/products/${editingProduct.id}`, values);
                message.success('Product updated successfully');
            } else {
                response = await axiosInstance.post('/products/register', values);
                message.success('Product registered successfully');
            }
            fetchProducts();
            setIsModalVisible(false);
            form.resetFields();
        } catch (error) {
            message.error('Failed to save product.');
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleDelete = async (id) => {
        try {
            await axiosInstance.delete(`/products/${id}`);
            message.success('Product deleted successfully');
            fetchProducts();
        } catch (error) {
            message.error('Failed to delete product.');
        }
    };

    const handleStatusUpdate = async (id, status) => {
        try {
            await axiosInstance.patch(`/products/${id}/status`, { status });
            message.success('Product status updated successfully');
            fetchProducts();
        } catch (error) {
            message.error('Failed to update product status.');
        }
    };

    const downloadImage = (imagePath, productName) => {
        // Remove any query parameters from the imagePath
        const cleanPath = imagePath.split('?')[0];
        const proxyUrl = `${BACKENDURL}/storage/${cleanPath}`;

        axiosInstance.get(proxyUrl, {
            responseType: "blob",
        })
            .then(response => {
                if (response.status !== 200) throw new Error("Failed to fetch image");
                return response.data;
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;

                // Create a clean filename from product name
                const cleanProductName = productName
                    .replace(/[^a-z0-9]/gi, '_') // Replace special chars with underscore
                    .toLowerCase();

                // Get file extension from original filename
                const fileExt = cleanPath.split('.').pop();

                // Set download filename (product_name.ext)
                a.download = `${cleanProductName}_barcode.${fileExt}`;

                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch(error => console.error("Error downloading image:", error));
    };


    const exportToExcel = async () => {
        try {

            // Prepare the data for export with updated quantities
            const data = products.map((product, index) => ({
                Name: product.name,
                'Buying Price': product.buying_price,
                'Selling Price': product.selling_price,
                Quantity: product.total_quantity,
                Category: product.category?.name || '',
                'Sub-Category': product.sub_category?.name || '',
                Barcode: product.barcode || '',
                Status: product.status?.status || '',
            }));

            // Convert data to worksheet
            const ws = XLSX.utils.json_to_sheet(data);

            // Create a new workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Products');

            // Generate the Excel file and trigger the download
            XLSX.writeFile(wb, 'products.xlsx');
            message.success('Products exported successfully!');
        } catch (error) {
            console.error("Error during export:", error.message);
            message.error('Failed to export products!');
        }
    };

    const exportToPDFd = async () => {
        try {
            exportToPDF(products);
        } catch (error) {
            console.error("Error during export:", error.message);
            message.error('Failed to export products!');
        }
    };

    const qrImageGenrt = async () => {
        try {
            const response = await axiosInstance.post('products/generate-qr-images');

            // Check if the request was successful
            if (response.data && response.data.message) {
                message.success(response.data.message); // Show success message from the backend
                window.location.reload();
            } else {
                message.success('Images generated successfully!'); // Fallback success message
            }
        } catch (error) {
            console.error("Error generating images:", error.response.data);

            // Show a user-friendly error message
            if (error.response && error.response.data && error.response.data.message) {
                message.error(error.response.data.message); // Show error message from the backend
            } else {
                message.error('Failed to generate images. Please try again.'); // Fallback error message
            }
        }
    };

    const openModal = async (id) => {
        try {
            const response = await axiosInstance.get(`/sales/sales-prices/${id}`);
            if (response.data) {
                setSelectedProductDetails(response.data.details);
                setCurrentProductIndex(id);
                setModalVisible(true);
            }
        } catch (error) {
            console.error('Failed to fetch product details:', error);
        }
    };

    const columns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Buying Price', dataIndex: 'buying_price', key: 'buying_price' },
        { title: 'Selling Price', dataIndex: 'selling_price', key: 'selling_price' },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (_, product) => (
                <>
                    <Button onClick={() => openModal(product.id)}>view quantity</Button>
                </>
            ),
        },
        { title: 'Category', dataIndex: ['category', 'name'], key: 'category' },
        { title: 'Sub-Category', dataIndex: ['sub_category', 'name'], key: 'sub_category' },
        { title: 'Status', dataIndex: ['status', 'status'], key: 'status' },
        { title: 'Barcode', dataIndex: 'barcode', key: 'barcode' },
        {
            title: 'QR Code Image',
            dataIndex: ['barcode_image', 'name'], // Assuming 'name' contains product name
            key: 'barcode_image',
            render: (text, record) => (
                record.barcode_image ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img
                            src={`${BACKENDURLFiles}${record.barcode_image}`}
                            alt="QR Code"
                            style={{ width: '100px', height: 'auto' }}
                        />
                        <DownloadOutlined
                            onClick={() => downloadImage(record.barcode_image, record.name)}
                            style={{ color: '#1890ff', fontSize: '18px', cursor: 'pointer' }}
                        />
                    </div>
                ) : (
                    <span>No QR Code Image</span>
                )
            ),
        },

        {
            title: 'Action',
            key: 'action',
            render: (_, product) => (
                <>
                    <Button type="link" onClick={() => showModal(product)}>
                        Edit
                    </Button>
                    <Button
                        type="link"
                        onClick={() =>
                            handleStatusUpdate(product.id, product.status?.status === 'active' ? 'inactive' : 'active')
                        }
                    >
                        {product.status?.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                </>
            ),
        },
    ];

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8, // Add space between elements
                    // alignItems: 'flex-start', // Align buttons to the right by default
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap', // Allow wrapping for smaller screens
                        gap: 8, // Add spacing between buttons
                        justifyContent: 'flex-end', // Align buttons to the right
                    }}
                >
                    <Button type="dashed" onClick={() => showModal()}>
                        Register Product
                    </Button>
                    <Button
                        type="dashed"
                        onClick={() => setImportModalVisible(true)}
                        style={{ marginLeft: 0 }}
                    >
                        Import Products
                    </Button>
                    <Button type="dashed" onClick={exportToExcel} style={{ marginLeft: 0 }}>
                        Export to Excel
                    </Button>
                    <Button type="dashed" onClick={exportToPDFd} style={{ marginLeft: 0 }}>
                        Export to PDF
                    </Button>
                    <Button type="dashed" onClick={qrImageGenrt} style={{ marginLeft: 0 }}>
                        Generate Qr Img
                    </Button>
                </div>

                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap', // Wrap inputs on smaller screens
                        gap: 16, // Space between inputs
                        justifyContent: 'flex-start', // Align inputs to the left
                    }}
                    className="input-container"
                >
                    <Input
                        name="name"
                        placeholder="Search by name"
                        onChange={handleSearch}
                        style={{
                            flex: '1 1 200px', // Flexible width, min 200px
                            maxWidth: '100%', // Avoid overflow
                        }}
                    />
                    <Select
                        name="category_id"
                        placeholder="Select category"
                        onChange={handleCategoryChange}
                        style={{
                            flex: '1 1 200px', // Flexible width, min 200px
                            maxWidth: '100%', // Avoid overflow
                        }}
                    >
                        {categories.map((category) => (
                            <Option key={category.id} value={category.id}>
                                {category.name}
                            </Option>
                        ))}
                    </Select>
                    <Select
                        name="sub_category_id"
                        placeholder="Select sub-category"
                        onChange={(value) =>
                            setSearchParams((prevState) => ({
                                ...prevState,
                                sub_category_id: value,
                            }))
                        }
                        style={{
                            flex: '1 1 200px', // Flexible width, min 200px
                            maxWidth: '100%', // Avoid overflow
                        }}
                    >
                        {subCategories.map((subCategory) => (
                            <Option key={subCategory.id} value={subCategory.id}>
                                {subCategory.name}
                            </Option>
                        ))}
                    </Select>
                </div>

            </div>


            <Table
                columns={columns}
                dataSource={Array.isArray(products) ? products : []}
                rowKey="id"
                pagination={{ pageSize: 50 }}
                scroll={{ x: 'max-content' }}
                loading={loading}
            />

            {/* Import Modal */}
            <Modal
                title="Import Products"
                visible={importModalVisible}
                onCancel={() => setImportModalVisible(false)}
                footer={null}
            >
                <Form>
                    <Form.Item label="Import File">
                        <Button
                            onClick={handleFileClick}
                            style={{ width: '100%' }}
                            disabled={loading} // Disable button while loading
                        >
                            {loading ? <Spin size="small" /> : 'Select File'}
                        </Button>
                        <input
                            id="import-file-input"
                            type="file"
                            style={{ display: 'none' }}
                            onChange={(e) => handleImport(e.target.files[0])}

                        />
                    </Form.Item>
                    <p>
                        Download template for import:
                        <a download onClick={handleDownloadTemplate}>
                            Download Template
                        </a>
                    </p>
                </Form>
            </Modal>

            <Modal
                title={editingProduct ? 'Edit Product' : 'Register New Product'}
                visible={isModalVisible}
                onOk={() => form.submit()}
                onCancel={handleCancel}
                confirmLoading={loading}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ quantity: 0 }}
                    onFinish={handleOk}
                >
                    <Form.Item
                        label="Product Name"
                        name="name"
                        rules={[{ required: true, message: 'Please input the product name!' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Buying Price"
                        name="buying_price"
                        rules={[{ required: true, message: 'Please input the buying price!' }]}
                    >
                        <Input type="number" />
                    </Form.Item>
                    <Form.Item
                        label="Selling Price"
                        name="selling_price"
                        rules={[{ required: true, message: 'Please input the selling price!' }]}
                    >
                        <Input type="number" />
                    </Form.Item>
                    {/* <Form.Item
                        label="Quantity"
                        name="quantity"
                        rules={[{ required: true, message: 'Please input the quantity!' }]}
                    >
                        <Input type="number" />
                    </Form.Item> */}
                    <Form.Item
                        label="Expire Date"
                        name="expire_date"
                    // rules={[{ required: true, message: 'Please select the expire date!' }]}
                    >
                        <Input type="date" />
                    </Form.Item>
                    <Form.Item
                        label="Category"
                        name="category_id"
                        rules={[{ required: true, message: 'Please select the category!' }]}
                    >
                        <Select
                            onChange={handleCategoryChange}
                            disabled={!!editingProduct}
                        >
                            {categories.map(category => (
                                <Option key={category.id} value={category.id}>
                                    {category.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label="Sub-Category"
                        name="sub_category_id"
                        rules={[{ required: true, message: 'Please select the sub-category!' }]}
                    >
                        <Select>
                            {subCategories.map(subCategory => (
                                <Option key={subCategory.id} value={subCategory.id}>
                                    {subCategory.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label="Barcode"
                        name="barcode"
                    // rules={[{ required: true, message: 'Please input the barcode!' }]}
                    >
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Product Quantity"
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
            >
                <Table
                    dataSource={selectedProductDetails}
                    columns={[
                        { title: 'Expire Date', dataIndex: 'expire_date', key: 'expire_date' },
                        { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
                    ]}
                    rowKey="id"
                    pagination={false}
                    footer={() => {
                        // Calculate the total quantity
                        const totalQuantity = selectedProductDetails.reduce((total, record) => total + record.quantity, 0);

                        return (
                            <div>
                                <strong>Total Quantity: {totalQuantity}</strong>
                            </div>
                        );
                    }}
                />
            </Modal>
        </div>
    );
};

export default Products;